import React, { useCallback, useEffect, useMemo, useState } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import PacienteCreacion from "../Components/Configuracion/ModalCreacion/PacienteCreacion";
import { EliminarPaciente, ListarPacientes } from "../Service/Service.paciente";
import { GetTokenDecoded } from "../utils/storeUsuario";
import usePermissions from "../hook/usePermissions";
import DataTable from "../Components/UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../Components/UI/DataTable/piezas";
import { ChipCategoria } from "../Components/UI/Chip";
import Boton from "../Components/UI/Boton";
import MenuAcciones from "../Components/UI/MenuAcciones";
import Modal from "../Components/UI/Modal/Modal";
import { useToast } from "../Components/UI/Aviso";
import { CampoTexto } from "../Components/UI/Campo";
import { IconoMas, IconoEditar, IconoEliminar, IconoVer, IconoBuscar } from "../Components/UI/Iconos";
import { filtrarPorTexto } from "../Components/UI/exportarTabla";
import BotonesExportar from "../Components/UI/BotonesExportar";
import { usePageSubtitulo } from "../context/PageHeaderContext";

// SISTEMA-DE-DISENO · pantalla migrada al kit (5ª). Patrón LISTADO.
//
// MIGRAR NO ES REDISEÑAR: `ListarPacientes` y `EliminarPaciente` se siguen
// llamando igual, los permisos (`pacientes.create|edit|delete|view`) son los
// mismos, el Offcanvas de alta/edición es el mismo y el historial clínico se
// sigue abriendo con la misma URL.
//
// Esta pantalla usaba `DataTableExportable`, que sólo ella consumía. Se
// sustituye por el DataTable del sistema, PERO conservando lo que aquel
// componente aportaba y que no es estilo: la búsqueda local sobre todos los
// campos y las exportaciones a Excel y PDF, extraídas tal cual a
// `UI/exportarTabla.js` para que el archivo generado sea idéntico.
//
// LO ÚNICO QUE SE AÑADE: eliminar un paciente PIDE CONFIRMACIÓN. Antes bastaba
// un clic en la papelera para borrarlo, sin aviso ni vuelta atrás. No es un
// rediseño del flujo: es la regla del kit para acciones destructivas aplicada
// a la acción más destructiva de la aplicación.

// Mismas columnas y encabezados que exportaba `DataTableExportable`.
const CAMPOS_EXPORTABLES = ["value", "label", "idpaciente", "cedula", "email", "direccion", "genero", "seguro_nombre", "plan_nombre"];
const ENCABEZADOS_PDF = {
  label: "Nombres",
  cedula: "Cédula",
  email: "Correo",
  direccion: "Dirección",
  genero: "Género",
  seguro_nombre: "Seguro",
  plan_nombre: "Plan",
};

const GENERO = { M: "Masculino", F: "Femenino" };

export default function Pacientes() {
  const [paciente, setPaciente] = useState([]);
  const [pacienteEdit, setPacienteEdit] = useState({});
  const [showPaciente, setShowPaciente] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [porEliminar, setPorEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // RBAC-CUTOVER-02: Crear/Editar/Eliminar dependen del permiso RBAC real
  // (backend: RequirePermission("pacientes.create|edit|delete")), nunca de
  // ES_DOCTOR/nivel_user_id (gate legacy retirado -- ver entregable).
  const { hasPermission } = usePermissions();
  const { mostrar } = useToast();

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    const pacint = await ListarPacientes(GetTokenDecoded().idclinica);
    if (pacint && pacint.success) {
      setPaciente(
        pacint.data.map((p) => ({
          value: p.idpaciente,
          label: p.nombres_completos,
          ...p,
          // Se aplanan los nombres de aseguradora y plan: la columna los pinta
          // sin bajar a la relación, y -- lo importante -- `filtrarPorTexto`
          // recorre los valores de la fila con `String(valor)`, asi que un
          // objeto anidado NO seria buscable ("[object Object]"). Aplanados, la
          // busqueda por nombre de seguro o de plan funciona.
          seguro_nombre: p.seguros?.nombre || "",
          plan_nombre: p.plan?.nombre || "",
        }))
      );
    } else {
      // ESTADO DE ERROR: antes, si la consulta fallaba, la pantalla se quedaba
      // con la tabla vacía y sin decir nada.
      setPaciente([]);
      setError(pacint?.message || "No se pudo obtener el listado de pacientes.");
    }
    setCargando(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const handleClosePaciente = async () => {
    setShowPaciente(false);
    await cargar();
  };

  const handleEditarPaciente = (p) => {
    setPacienteEdit(p);
    setShowPaciente(true);
  };

  const handleShow = () => {
    setPacienteEdit(null);
    setShowPaciente(true);
  };

  const confirmarEliminar = async () => {
    const p = porEliminar;
    if (!p) return;
    setEliminando(true);
    const res = await EliminarPaciente(p.idpaciente);
    setEliminando(false);
    setPorEliminar(null);
    if (res && res.success) {
      mostrar({ texto: "Paciente eliminado" });
      await cargar();
    } else {
      mostrar({ tipo: "error", texto: res?.message || "No se pudo eliminar el paciente." });
    }
  };

  const handleHistorialClinico = (p) => {
    // redireccionar a la pagina de paciente_historial?idpaciente=${p.idpaciente}
    window.location.href = `/paciente_historial?idpaciente=${p.idpaciente}`;
  };

  const filtrados = useMemo(() => filtrarPorTexto(paciente, busqueda), [paciente, busqueda]);
  const pagina = useMemo(() => filtrados.slice((page - 1) * pageSize, page * pageSize), [filtrados, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [busqueda]);

  usePageSubtitulo(cargando ? "" : `${filtrados.length} paciente${filtrados.length === 1 ? "" : "s"}`);

  const columnas = [
    {
      key: "nombres_completos",
      titulo: "Paciente",
      render: (row) => (
        <CeldaDoble
          principal={tituloDeNombre(row.nombres_completos)}
          secundario={[row.cedula, row.estado_civil].filter(Boolean).join(" · ")}
          monoSecundario
        />
      ),
    },
    {
      key: "email",
      titulo: "Contacto",
      ancho: 260,
      render: (row) => (
        <CeldaDoble principal={row.email} secundario={[row.celular, row.telefono_familiar].filter(Boolean).join(" · ")} />
      ),
    },
    { key: "direccion", titulo: "Dirección", ancho: 240, render: (row) => <Celda valor={row.direccion} /> },
    {
      key: "genero",
      titulo: "Género",
      ancho: 130,
      render: (row) =>
        GENERO[row.genero] ? (
          <ChipCategoria>{GENERO[row.genero]}</ChipCategoria>
        ) : (
          // Un género ausente o fuera del catálogo se señala, no se disfraza.
          <ChipCategoria incompleto>{row.genero || "Sin registrar"}</ChipCategoria>
        ),
    },
    {
      // Seguro y plan van JUNTOS en una sola columna porque son un solo dato:
      // el plan no existe sin la aseguradora que lo emite. Los colores no son
      // decorativos -- azul = aseguradora, verde = plan -- y un dato que falta
      // se marca en ambar con icono, la regla del kit para datos incompletos.
      key: "seguro_nombre",
      titulo: "Seguro / Plan",
      ancho: 230,
      render: (row) => {
        if (!row.seguro_nombre) {
          return <ChipCategoria incompleto>Sin seguro</ChipCategoria>;
        }
        return (
          <span className="kchips-pila">
            <ChipCategoria tono="info" title={`Aseguradora: ${row.seguro_nombre}`}>
              {row.seguro_nombre}
            </ChipCategoria>
            {row.plan_nombre ? (
              <ChipCategoria tono="exito" title={`Plan: ${row.plan_nombre}`}>
                {row.plan_nombre}
              </ChipCategoria>
            ) : (
              // Tiene aseguradora pero no plan: es una ficha a medio llenar y
              // se dice, no se deja el hueco en blanco.
              <ChipCategoria incompleto title="El paciente tiene aseguradora pero no plan asignado">
                Sin plan
              </ChipCategoria>
            )}
          </span>
        );
      },
    },
    {
      key: "accion",
      titulo: "",
      ancho: 150,
      alinear: "derecha",
      render: (row) => {
        const secundarias = [];
        if (hasPermission("pacientes.edit")) {
          secundarias.push({ key: "editar", label: "Editar paciente", icono: IconoEditar, onClick: () => handleEditarPaciente(row) });
        }
        if (hasPermission("pacientes.delete")) {
          if (secundarias.length > 0) secundarias.push({ separador: true });
          secundarias.push({
            key: "eliminar",
            label: "Eliminar paciente",
            icono: IconoEliminar,
            peligro: true,
            onClick: () => setPorEliminar(row),
          });
        }
        return (
          <span className="ac-celda-accion">
            <Boton
              tamano="sm"
              soloIcono
              icono={IconoVer}
              titulo="Ver historial clínico"
              onClick={(e) => {
                e.stopPropagation();
                handleHistorialClinico(row);
              }}
              data-testid={`paciente-historial-${row.idpaciente}`}
            />
            <MenuAcciones acciones={secundarias} titulo={`Más acciones de ${row.nombres_completos}`} />
          </span>
        );
      },
    },
  ];

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <div className="ac-filtros-linea mb-3">
            <CampoTexto
              className="ac-filtros-buscador"
              etiqueta="Búsqueda"
              icono={IconoBuscar}
              placeholder="Nombre, cédula, correo…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              data-testid="pacientes-busqueda"
            />
            <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "flex-end" }}>
              {/* EXPORTACION-LISTADOS-01: la descarga exige `pacientes.export`,
                  un permiso propio del catalogo asignable por rol. Antes Excel
                  se gateaba con `pacientes.view` -- que tiene cualquiera que
                  vea esta pantalla, o sea, nadie quedaba fuera -- y el PDF no
                  se gateaba en absoluto. Sin el permiso los botones se ven
                  APAGADOS, con el motivo en el tooltip. */}
              <BotonesExportar
                filas={filtrados}
                campos={CAMPOS_EXPORTABLES}
                titulo="Listado de Pacientes"
                encabezados={ENCABEZADOS_PDF}
                permiso="pacientes.export"
                idPrefijo="pacientes"
              />
              {hasPermission("pacientes.create") && (
                <Boton variante="primario" icono={IconoMas} onClick={handleShow} data-testid="pacientes-crear">
                  Crear paciente
                </Boton>
              )}
            </span>
          </div>

          <DataTable
            columnas={columnas}
            filas={pagina}
            filaKey="idpaciente"
            cargando={cargando}
            error={error}
            onReintentar={cargar}
            etiquetaTabla="Listado de pacientes"
            hayFiltrosAplicados={Boolean(busqueda)}
            onLimpiarFiltros={() => setBusqueda("")}
            textoVacioSinFiltros="Todavía no hay pacientes registrados en esta clínica."
            paginacion={{
              page,
              pageSize,
              total: filtrados.length,
              tamanos: [10, 25, 50, 100],
              onCambio: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
          />
        </div>
      </div>

      {/* Eliminar un paciente no tenía confirmación: un clic y desaparecía. */}
      <Modal
        abierto={!!porEliminar}
        titulo="¿Eliminar este paciente?"
        ancho="confirmacion"
        destructivo
        onCerrar={() => setPorEliminar(null)}
        secundario={{ label: "Cancelar" }}
        primario={{ label: "Eliminar paciente", onClick: confirmarEliminar, cargando: eliminando }}
      >
        <p data-testid="paciente-confirmacion">
          Se eliminará a <strong>{porEliminar?.nombres_completos}</strong> ({porEliminar?.cedula}). Esta acción no se puede
          deshacer desde la aplicación.
        </p>
      </Modal>

      <Offcanvas
        style={{
          width: "50%",
          zIndex: 9999,
        }}
        placement="end"
        show={showPaciente}
        onHide={handleClosePaciente}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{pacienteEdit ? "Actualizar" : "Crear"} Paciente</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <PacienteCreacion
            handleClosePaciente={handleClosePaciente}
            showPaciente={showPaciente}
            idclinica={GetTokenDecoded().idclinica}
            pacienteEdit={pacienteEdit}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
